/**
 * Material Answer Modal Component
 *
 * Allows users to link/unlink answers to materials.
 * Teachers can only link to their own materials.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ModalOverlay, ModalContainer } from './BaseModal';
import {
  getLinkedAnswers,
  linkAnswer,
  unlinkAnswer,
  listAvailableAnswers
} from '../../api/materialPortalApi';

// Styled components
const Modal = styled(ModalContainer)`
  max-width: 700px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 12px 12px 0 0;

  h3 {
    margin: 0;
    font-size: 18px;
    color: white;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  line-height: 1;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: end;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$color || '#f59e0b'};
  color: white;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AnswerCard = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AnswerInfo = styled.div`
  flex: 1;

  h5 {
    margin: 0 0 4px 0;
    font-size: 15px;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .meta {
    font-size: 12px;
    color: #6b7280;
  }
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: #fef3c7;
  color: #92400e;
`;

const RemoveButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #ef4444;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  background: #fef2f2;
  color: #dc2626;

  &:hover:not(:disabled) {
    background: #fee2e2;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const LinkedByInfo = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
`;

const MaterialAnswerModal = ({
  show,
  onClose,
  material
}) => {
  const [linkedAnswers, setLinkedAnswers] = useState([]);
  const [availableAnswers, setAvailableAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [selectedAnswerId, setSelectedAnswerId] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    if (!material?.id) return;

    setLoading(true);
    setError(null);

    try {
      const [linksData, answersData] = await Promise.all([
        getLinkedAnswers(material.id),
        listAvailableAnswers()
      ]);

      setLinkedAnswers(linksData);

      // Filter out already linked answers
      const linkedIds = linksData.map(l => l.answerId);
      const available = answersData.filter(a => !linkedIds.includes(a.id));
      setAvailableAnswers(available);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [material?.id]);

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show, loadData]);

  // Handle link answer
  const handleLink = async () => {
    if (!selectedAnswerId) {
      alert('Please select an answer to link');
      return;
    }

    setSaving(true);
    try {
      await linkAnswer(material.id, selectedAnswerId);

      // Refresh data to get updated lists
      await loadData();
      setSelectedAnswerId('');

    } catch (err) {
      console.error('Error linking answer:', err);
      alert(`Failed to link answer: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle unlink answer
  const handleUnlink = async (linkId) => {
    if (!window.confirm('Are you sure you want to unlink this answer?')) {
      return;
    }

    setSaving(true);
    try {
      await unlinkAnswer(linkId);

      // Refresh data
      await loadData();

    } catch (err) {
      console.error('Error unlinking answer:', err);
      alert(`Failed to unlink answer: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <ModalOverlay>
      <Modal>
        <ModalHeader>
          <h3>Answers - {material?.name}</h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && <ErrorState>{error}</ErrorState>}

          {loading ? (
            <LoadingState>Loading...</LoadingState>
          ) : (
            <>
              {/* Link New Answer Section */}
              <Section>
                <SectionTitle>Link Answer</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <label>Select an answer to link</label>
                    <Select
                      value={selectedAnswerId}
                      onChange={(e) => setSelectedAnswerId(e.target.value)}
                    >
                      <option value="">Select an answer...</option>
                      {availableAnswers.map(answer => (
                        <option key={answer.id} value={answer.id}>
                          {answer.name} (Y{answer.year}T{answer.term}W{answer.week} - {answer.subject})
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                  <Button
                    onClick={handleLink}
                    disabled={saving || !selectedAnswerId}
                  >
                    Link
                  </Button>
                </FormRow>
                {availableAnswers.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    No available answers to link. Upload materials with "Is Answer" checked to create answers.
                  </div>
                )}
              </Section>

              {/* Linked Answers Section */}
              <Section>
                <SectionTitle>Linked Answers ({linkedAnswers.length})</SectionTitle>
                {linkedAnswers.length === 0 ? (
                  <EmptyState>
                    No answers linked to this material yet.
                  </EmptyState>
                ) : (
                  linkedAnswers.map(link => (
                    <AnswerCard key={link.id}>
                      <AnswerInfo>
                        <h5>
                          {link.answerName || 'Unknown Answer'}
                          <Badge>Answer</Badge>
                        </h5>
                        <div className="meta">
                          Year {link.answerYear} | Term {link.answerTerm} | Week {link.answerWeek} | {link.answerSubject}
                        </div>
                        <LinkedByInfo>
                          Linked by {link.linkedByName || 'Unknown'} on {new Date(link.createdAt).toLocaleString()}
                        </LinkedByInfo>
                      </AnswerInfo>
                      <RemoveButton
                        onClick={() => handleUnlink(link.id)}
                        disabled={saving}
                      >
                        Unlink
                      </RemoveButton>
                    </AnswerCard>
                  ))
                )}
              </Section>
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <Button $color="#6b7280" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </ModalOverlay>
  );
};

export default MaterialAnswerModal;
